import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import clsx from 'clsx';
import { Camera, Clock, LogIn, LogOut, MapPin, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppSelector } from '@/app/hooks';
import api, { getErrorMessage } from '@/services/api';
import { ApiResponse, Attendance, Staff } from '@/services/types';
import { API_ENDPOINTS } from '@/utils/constants';
import { Alert, Avatar, Badge, Button, Input, LoadingSkeleton } from '@/components/ui';
import { PageShell } from '@/components/layout/PageShell';
import { distanceMeters } from '@/utils/geo';

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const getPosition = () =>
    new Promise<GeolocationPosition>((resolve, reject) => {
        if (!navigator.geolocation) { reject(new Error('unavailable')); return; }
        navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
    });

/** Attendance routes return meta.errors as a plain string on failure. */
const attendanceError = (err: unknown) => {
    if (axios.isAxiosError(err)) {
        const errors = err.response?.data?.meta?.errors;
        if (typeof errors === 'string' && errors) return errors;
    }
    return getErrorMessage(err);
};

const timeOf = (iso: string) => new Date(iso).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });

/**
 * 14 Absensi (pick name) → 15 selfie & check-in. Camera via <input capture="user">; location via geolocation.
 * ponytail: no mock-location detection on web (no browser API for it); server has the distance anyway.
 */
const AttendancePage: React.FC = () => {
    const { t } = useTranslation();
    const { selectedShop, shopConfig } = useAppSelector((s) => s.auth);
    const [distanceM, setDistanceM] = useState<number | null>(null);
    const [staff, setStaff] = useState<Staff[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [picked, setPicked] = useState<Staff | null>(null);
    const [step, setStep] = useState<'pick' | 'selfie'>('pick');
    const [photo, setPhoto] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [now, setNow] = useState(() => new Date());
    const fileRef = useRef<HTMLInputElement>(null);

    const load = useCallback(async () => {
        if (!selectedShop) return;
        setLoading(true);
        setError(null);
        try {
            const res = await api.get<ApiResponse<Staff[]>>(API_ENDPOINTS.ATTENDANCE_STAFF(selectedShop.shopId));
            setStaff(res.data.data || []);
        } catch (e) {
            setError(getErrorMessage(e));
        } finally {
            setLoading(false);
        }
    }, [selectedShop]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { const id = setInterval(() => setNow(new Date()), 30_000); return () => clearInterval(id); }, []);
    useEffect(() => {
        if (!photo) { setPreview(null); return; }
        const url = URL.createObjectURL(photo);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [photo]);

    if (!selectedShop) return null;

    const roleLabel = (role: string) => t(`attendance.roles.${role.toUpperCase()}` as 'attendance.roles.CASHIER', { defaultValue: role });
    /** Live "≈ N m dari toko" chip; the server recomputes on submit, this is just feedback */
    const readDistance = () => {
        setDistanceM(null);
        if (!shopConfig?.latitude || !shopConfig?.longitude) return;
        getPosition().then((pos) => setDistanceM(distanceMeters(pos.coords.latitude, pos.coords.longitude, shopConfig.latitude, shopConfig.longitude))).catch(() => undefined);
    };

    const isCheckOut = !!picked?.checkedInAt;
    const reset = () => { setStep('pick'); setPicked(null); setPhoto(null); setNote(''); setFormError(null); };

    const submit = async () => {
        if (!picked) return;
        setFormError(null);
        if (!photo) { setFormError(t('attendance.photoRequired')); return; }
        if (photo.size > MAX_IMAGE_BYTES) { setFormError(t('attendance.photoTooLarge')); return; }
        setSubmitting(true);
        try {
            let pos: GeolocationPosition;
            try {
                pos = await getPosition();
            } catch {
                setFormError(t('attendance.locationRequired'));
                return;
            }
            const form = new FormData();
            form.append('image', photo, 'selfie.jpg');
            form.append('posUserId', String(picked.posUserId));
            form.append('lat', String(pos.coords.latitude));
            form.append('lon', String(pos.coords.longitude));
            if (note.trim()) form.append('note', note.trim());
            const url = isCheckOut ? API_ENDPOINTS.ATTENDANCE_CHECK_OUT(selectedShop.shopId) : API_ENDPOINTS.ATTENDANCE_CHECK_IN(selectedShop.shopId);
            const res = await api.post<ApiResponse<Attendance>>(url, form, { headers: { 'Content-Type': 'multipart/form-data' } });
            const d = res.data.data;
            const dist = isCheckOut ? d.checkOutDistanceM : d.checkInDistanceM;
            toast.success(t(isCheckOut ? 'attendance.checkedOut' : 'attendance.checkedIn', { name: d.userName, distance: dist != null ? Math.round(dist) : '-' }));
            reset();
            load();
        } catch (e) {
            const msg = attendanceError(e);
            if (msg === 'Already checked in' || msg === 'No open check-in') {
                toast.error(t(msg === 'Already checked in' ? 'attendance.alreadyCheckedIn' : 'attendance.noOpenCheckIn'));
                reset();
                load();
            } else if (msg.startsWith('image')) {
                setFormError(msg.startsWith('image upload failed') ? t('attendance.uploadFailed') : t('attendance.photoTooLarge'));
                setPhoto(null);
            } else {
                setFormError(msg);
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (step === 'selfie' && picked) {
        return (
            <PageShell
                dark
                title={isCheckOut ? t('attendance.checkOut') : t('attendance.checkIn')}
                subtitle={`${picked.userName} · ${roleLabel(picked.role)}`}
                onBack={() => setStep('pick')}
                padded={false}
            >
                <div className="flex h-full min-h-0 flex-col lg:flex-row">
                    <div className="flex min-h-0 flex-1 flex-col px-5 pb-2 lg:p-[0_24px_24px]">
                        <input ref={fileRef} type="file" accept="image/*" capture="user" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] ?? null)} />
                        <button type="button" onClick={() => fileRef.current?.click()} className="relative h-[380px] w-full overflow-hidden rounded-2xl bg-[#2a1a25] lg:h-full lg:min-h-0 lg:flex-1">
                            {preview ? <img src={preview} alt="" className="h-full w-full object-cover" /> : (
                                <span className="absolute inset-0 flex items-center justify-center">
                                    <span className="h-[250px] w-[190px] rounded-full border-[3px] border-dashed border-accent-300 lg:h-[340px] lg:w-[260px]" />
                                </span>
                            )}
                            <span className="absolute inset-x-0 bottom-4 flex items-center justify-center gap-2 text-[13px] font-bold text-primary-200"><Camera className="h-4 w-4" />{preview ? t('attendance.retake') : t('attendance.tapToCapture')}</span>
                        </button>
                    </div>
                    <div className="flex shrink-0 flex-col gap-3 rounded-sheet bg-surface-card px-5 pb-[max(28px,env(safe-area-inset-bottom))] pt-5 text-ink-primary lg:w-[420px] lg:rounded-none lg:p-[32px_28px]">
                        <div className="hidden items-center gap-3.5 lg:flex">
                            <Avatar name={picked.userName} src={picked.userAvatar} size="lg" />
                            <span className="flex min-w-0 flex-col"><span className="truncate text-xl font-black">{picked.userName}</span><span className="text-sm text-ink-muted">{roleLabel(picked.role)} · {selectedShop.shopName}</span></span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <span className="flex h-[34px] items-center gap-1.5 rounded-pill bg-status-success-soft px-3 text-[13px] font-extrabold text-success-700 tnum"><MapPin className="h-[15px] w-[15px]" />{distanceM != null ? t('attendance.distance', { m: Math.round(distanceM) }) : selectedShop.shopName}</span>
                            <span className="flex h-[34px] items-center gap-1.5 rounded-pill bg-surface-sunken px-3 text-[13px] font-extrabold text-ink-secondary tnum"><Clock className="h-[15px] w-[15px]" />{now.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <p className="text-xs text-ink-muted">{t('attendance.locationHint')}</p>
                        <Input fieldSize="sm" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('attendance.notePlaceholder')} />
                        {formError && <Alert variant="error" message={formError} />}
                        <div className="hidden flex-1 lg:block" />
                        <div className="grid grid-cols-[auto_1fr] gap-2.5 lg:grid-cols-1">
                            <Button variant="outline" size="lg" icon={<RotateCcw />} onClick={() => { setPhoto(null); fileRef.current?.click(); }} className="lg:order-2">{t('attendance.retake')}</Button>
                            <Button variant="primary" size="lg" fullWidth icon={isCheckOut ? <LogOut /> : <LogIn />} onClick={submit} loading={submitting} className="lg:order-1">{isCheckOut ? t('attendance.checkOut') : t('attendance.checkIn')}</Button>
                        </div>
                    </div>
                </div>
            </PageShell>
        );
    }

    return (
        <PageShell
            title={t('attendance.title')}
            subtitle={t('attendance.pickHint')}
            backTo="/settings"
            footer={<Button variant="primary" size="lg" fullWidth icon={<Camera />} disabled={!picked} onClick={() => { setPhoto(null); setFormError(null); setStep('selfie'); readDistance(); }} className="lg:!w-auto lg:!px-8 lg:ml-auto lg:flex">{t('attendance.continueSelfie')}</Button>}
        >
            <div className="flex flex-col gap-2.5 pb-5 lg:mx-auto lg:grid lg:max-w-[760px] lg:grid-cols-2 lg:gap-3">
                {error && <Alert variant="error" message={error} className="lg:col-span-2" />}
                {loading && [1, 2, 3].map((i) => <LoadingSkeleton key={i} height={72} />)}
                {!loading && staff.length === 0 && <p className="py-12 text-center text-sm text-ink-muted lg:col-span-2">{t('attendance.noStaff')}</p>}
                {staff.map((s) => {
                    const active = picked?.posUserId === s.posUserId;
                    return (
                        <button
                            key={s.posUserId}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            onClick={() => setPicked(s)}
                            className={clsx('flex items-center gap-3 rounded-lg px-3.5 py-3 text-left lg:p-4', active ? 'border-2 border-accent-500 bg-accentsoft' : 'border border-line-subtle bg-surface-card')}
                        >
                            <Avatar name={s.userName} src={s.userAvatar} size="lg" status={s.checkedInAt ? 'online' : undefined} />
                            <span className="flex min-w-0 flex-1 flex-col"><span className="truncate text-[15px] font-extrabold">{s.userName}</span><span className="text-[13px] text-ink-muted">{roleLabel(s.role)}</span></span>
                            {s.checkedInAt ? <Badge tone="success" dot>{t('attendance.checkedInAt', { time: timeOf(s.checkedInAt) })}</Badge> : <Badge tone="neutral">{t('attendance.notCheckedIn')}</Badge>}
                        </button>
                    );
                })}
            </div>
        </PageShell>
    );
};

export default AttendancePage;
